<template>
  <div>
    <div class="tile is-parent">
      <article class="tile is-child box">

        <div class="columns">
          <div class="column">
            <div class="fixed-grid has-12-cols">
              <div class="grid is-col-min-15">
                <!-- Left Column: System Messages List -->
                <div class="cell is-col-span-6 left-column">
                  <article class="box detail-page-tile is-flex-grow-1 is-flex is-flex-direction-column pt-2"
                    style="max-height: calc(120vh - 8rem);">
                    <!-- <p class="title">System Messages</p> -->

                    <div class="tabs mb-4">
                      <ul>
                        <li class="sub-title"
                          :class="{ 'is-active': selectedTab === 'current' }">
                          <a @click.prevent="selectedTab = 'current'">
                            <span>System Messages</span>
                          </a>
                        </li>
                        <li class="sub-title"
                          :class="{ 'is-active': selectedTab === 'archived' }">
                          <a @click.prevent="selectedTab = 'archived'">
                            <span>Archived Messages</span>
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div v-if="isLoading"
                      class="has-text-centered p-4">
                      <span class="icon is-large">
                        <i class="mdi mdi-loading mdi-spin mdi-24px" />
                      </span>
                      <p class="mt-2">Loading messages...</p>
                    </div>

                    <div v-else
                      ref="messagesContainer"
                      class="is-flex-grow-1 is-overflow-y-auto pt-4 pr-4"
                      style="max-height: calc(120vh);"
                      @scroll="onScroll">
                      <div v-if="filteredMessages.length === 0"
                        class="has-text-centered p-4 has-text-grey">
                        <span class="msr msr-36px has-text-grey">
                          add_comment
                        </span>
                        <p class="mt-2">
                          {{ selectedTab === 'current'
                            ? 'No system-wide messages are currently published.'
                            : 'No archived messages.' }}
                        </p>
                        <p class="mt-2"
                          v-if="selectedTab === 'current'">
                          Add a new message to keep users informed of any issues or announcements.
                        </p>
                      </div>

                      <div v-for="message in filteredMessages"
                        :key="message.systemMessageId"
                        class="message-card mb-6"
                        style="position: relative;">
                        <span class="is-size-7 has-text-grey"
                          style="position: absolute; top: -1.2rem; left: 0;">
                          {{ message.modifiedByName || message.createdByName }} - {{ $filters.formatDateTimeLocale(message.modifiedDate || message.createdDate, $userInfo.locale) }}
                        </span>
                        <div class="is-flex is-justify-content-space-between is-align-items-start mb-2">
                          <div class="is-flex is-align-items-center">
                            <span class="message-indicator mr-2"
                              :class="getMessageTypeClass(message.type)" />
                            <div class="message-title has-text-weight-semibold">
                              {{ message.title }}
                            </div>
                          </div>
                          <div class="is-flex is-align-items-center">
                            <span v-if="message.isDraft"
                              class="tag is-rounded mr-2">
                              Draft
                            </span>
                            <span v-else-if="message.active"
                              class="tag is-success mr-2 has-text-light">
                              <span class="icon">
                                <span class="msr msr-16px">check_circle</span>
                              </span>
                              &nbsp;Active
                            </span>
                            <span v-else>
                              <span class="tag is-danger mr-2 has-text-light">
                                <span class="icon">
                                  <span class="msr msr-16px">do_not_disturb_on</span>
                                </span>
                                &nbsp;Inactive
                              </span>
                            </span>
                            <div v-if="!message.isDraft && selectedTab === 'current'"
                              class="pretty p-switch p-fill">
                              <input type="checkbox"
                                @change="handleToggleChange(message, $event)"
                                :checked="!!message.active">
                              <div class="state p-primary">
                                <label></label>
                              </div>
                            </div>
                            <button class="button is-small is-white tooltip is-tooltip-bottom ml-1"
                              data-tooltip="Edit"
                              @click="editMessage(message)">
                              <span class="icon">
                                <i class="mdi mdi-pencil mdi-18px" />
                              </span>
                            </button>
                            <button class="button is-small is-white tooltip is-tooltip-bottom ml-1"
                              data-tooltip="Delete"
                              @click="confirmDelete(message)">
                              <span class="icon has-text-danger">
                                <i class="mdi mdi-delete mdi-18px" />
                              </span>
                            </button>
                            <button v-if="selectedTab === 'archived'"
                              class="button is-small is-white tooltip is-tooltip-bottom ml-1"
                              data-tooltip="Restore"
                              @click="restoreMessage(message)">
                              <span class="icon has-text-primary">
                                <i class="mdi mdi-restore mdi-18px" />
                              </span>
                            </button>
                          </div>
                        </div>

                        <div class="message-content mb-2 is-size-7">
                          {{ truncateText(message.caption, 150) }}
                        </div>

                        <div v-if="message.startAtUtc || message.endAtUtc"
                          class="is-flex is-align-items-center is-size-7 has-text-grey">
                          <span class="icon is-small mr-1">
                            <i class="mdi mdi-clock-outline mdi-14px" />
                          </span>
                          <span>
                            {{ formatDisplayDate(message.startAtUtc) }} - {{ formatDisplayDate(message.endAtUtc) }}
                          </span>
                        </div>
                      </div>

                      <div v-if="isLoadingMore"
                        class="has-text-centered p-4">
                        <span class="icon">
                          <i class="mdi mdi-loading mdi-spin mdi-24px" />
                        </span>
                        <span class="ml-2">Loading more...</span>
                      </div>

                      <div v-if="!hasMoreMessages && filteredMessages.length > 0"
                        class="has-text-centered p-4 has-text-grey is-size-7">
                        <span>— End of messages —</span>
                      </div>
                    </div>
                  </article>
                </div>

                <!-- Right Column: Create/Edit Form -->
                <div class="cell is-col-span-6">
                  <article class="box detail-page-tile is-flex is-flex-direction-column">
                    <p class="title">{{ isEditing ? 'Edit Message' : 'Create New Message' }}</p>

                    <div class="is-flex-grow-1">
                      <div class="field">
                        <label class="label">Message Title <span class="has-text-danger">*</span></label>
                        <div class="control">
                          <input v-model="innerEntity.title"
                            class="input"
                            type="text"
                            placeholder="Add message title"
                            :class="{ 'is-danger': $v.innerEntity.title.$error }">
                        </div>
                        <p v-if="!$v.innerEntity.title.required && $v.innerEntity.title.$dirty"
                          class="help is-danger">Message title is required</p>
                      </div>

                      <div class="field">
                        <label class="label">Short Message <span class="has-text-danger">*</span></label>
                        <div class="control">
                          <textarea v-model="innerEntity.caption"
                            class="textarea"
                            rows="3"
                            placeholder="Brief message shown by default"
                            :class="{ 'is-danger': $v.innerEntity.caption.$error }" />
                        </div>
                        <p v-if="!$v.innerEntity.caption.required && $v.innerEntity.caption.$dirty"
                          class="help is-danger">Short message is required</p>
                      </div>

                      <div class="field">
                        <label class="label">Detailed Message (optional)</label>
                        <div class="control">
                          <textarea v-model="innerEntity.message"
                            class="textarea"
                            rows="4"
                            placeholder="Additional details shown when the user clicks 'Read More'" />
                        </div>
                      </div>

                      <div class="field">
                        <label class="label">Message Type <span class="has-text-danger">*</span></label>
                        <div class="control">
                          <div class="select is-fullwidth"
                            :class="{ 'is-danger': $v.innerEntity.type.$error }">
                            <select v-model="innerEntity.type">
                              <option value="">Select</option>
                              <option v-for="type in messageTypes"
                                :key="type.value"
                                :value="type.value">
                                {{ type.label }}
                              </option>
                            </select>
                          </div>
                        </div>
                        <p v-if="!$v.innerEntity.type.required && $v.innerEntity.type.$dirty"
                          class="help is-danger">Message type is required</p>
                      </div>

                      <div class="field">
                        <label class="label">Display Duration <span class="has-text-danger">*</span></label>
                        <div class="columns is-mobile">
                          <div class="column">
                            <v-date-picker v-model="innerEntity.startAtUtc"
                              :is-dark="$theme.isDark"
                              :masks="dateFormats"
                              :attributes="dateAttrs"
                              :locale="$userInfo.locale"
                              :timezone="$filters.getIanaTimezone()"
                              :minute-increment="5"
                              mode="dateTime">
                              <template v-slot="{ inputValue, inputEvents }">
                                <div class="control has-icons-left">
                                  <input type="text"
                                    class="input"
                                    placeholder="Start Date & Time"
                                    :value="inputValue"
                                    v-on="inputEvents"
                                    :class="{ 'is-danger': $v.innerEntity.startAtUtc.$error }">
                                  <span class="icon is-small is-left">
                                    <i class="mdi mdi-calendar mdi-18px" />
                                  </span>
                                </div>
                                <p v-if="!$v.innerEntity.startAtUtc.required && $v.innerEntity.startAtUtc.$dirty"
                                  class="help is-danger">Start date & time is required</p>
                                <p v-if="$v.innerEntity.startAtUtc.required && $v.innerEntity.startAtUtc.$error && !$v.innerEntity.startAtUtc.minDate"
                                  class="help is-danger">Start date must be today or later</p>
                              </template>
                            </v-date-picker>
                          </div>
                          <div class="column">
                            <v-date-picker v-model="innerEntity.endAtUtc"
                              :is-dark="$theme.isDark"
                              :masks="dateFormats"
                              :attributes="dateAttrs"
                              :locale="$userInfo.locale"
                              :timezone="$filters.getIanaTimezone()"
                              :minute-increment="5"
                              mode="dateTime">
                              <template v-slot="{ inputValue, inputEvents }">
                                <div class="control has-icons-left">
                                  <input type="text"
                                    class="input"
                                    placeholder="End Date & Time"
                                    :value="inputValue"
                                    v-on="inputEvents"
                                    :class="{ 'is-danger': $v.innerEntity.endAtUtc.$error }">
                                  <span class="icon is-small is-left">
                                    <i class="mdi mdi-calendar mdi-18px" />
                                  </span>
                                </div>
                                <p v-if="!$v.innerEntity.endAtUtc.required && $v.innerEntity.endAtUtc.$dirty"
                                  class="help is-danger">End date & time is required</p>
                                <p v-if="$v.innerEntity.endAtUtc.required && $v.innerEntity.endAtUtc.$error && !$v.innerEntity.endAtUtc.greaterThanStartDate"
                                  class="help is-danger">End date must be after start date</p>
                              </template>
                            </v-date-picker>
                          </div>
                        </div>
                      </div>

                      <hr class="my-4">

                      <div class="field">
                        <label class="label">Link Text</label>
                        <div class="control">
                          <input v-model="innerEntity.linkText"
                            class="input"
                            type="text"
                            placeholder="Add text for link">
                        </div>
                      </div>

                      <div class="field">
                        <label class="label">Link URL</label>
                        <div class="control">
                          <input v-model="innerEntity.linkUrl"
                            class="input"
                            type="text"
                            placeholder="Add link URL">
                        </div>
                      </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="is-flex is-justify-content-flex-end pt-4"
                      style="border-top: 1px solid var(--bulma-border);">
                      <button v-if="isEditing"
                        class="button mr-2"
                        @click="cancelEdit">
                        <span class="msr msr-18px mr-1">
                          close
                        </span>
                        <span>Cancel</span>
                      </button>
                      <button class="button mr-2"
                        @click="saveAsDraft">
                        <span class="msr msr-18px mr-1">
                          edit
                        </span>
                        <span>Save as Draft</span>
                      </button>
                      <button class="button is-success"
                        @click="saveAndActivate">
                        <span class="msr msr-18px mr-1">
                          save
                        </span>
                        <span>Save & Activate</span>
                      </button>
                    </div>
                  </article>
                </div>
              </div>

              <base-modal-ex :active.sync="isDeleteModalActive"
                :animation-in="'zoomIn'"
                :animation-out="'zoomOut'"
                :animation-duration="0.3"
                :can-cancel="false"
                :min-width="40"
                has-modal-card>
                <header class="modal-card-head">
                  <p class="modal-card-title">Confirm Delete</p>
                </header>
                <section class="modal-card-body">
                  <p>Are you sure you want to delete this system message?</p>
                  <p class="has-text-weight-semibold mt-2">{{ messageToDelete?.title }}</p>
                </section>
                <footer class="modal-card-foot is-flex is-justify-content-flex-end">
                  <button class="button mr-2"
                    @click="isDeleteModalActive = false">
                    <span class="msr msr-18px mr-1">
                      close
                    </span>
                    Cancel
                  </button>
                  <button class="button is-danger"
                    @click="deleteMessage">
                    <span class="msr msr-18px mr-1">
                      delete
                    </span>
                    <span>Delete</span>
                  </button>
                </footer>
              </base-modal-ex>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { BaseModalEx } from '@/components/BulmaModal'
import SystemMessageService from './systemMessageService'
import StoreMixin from './storeMixin'
import SystemMessageValidation from './SystemMessageValidation'
import _cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'SystemMessageDetail',
  components: {
    BaseModalEx
  },
  mixins: [StoreMixin, SystemMessageValidation],
  data() {
    return {
      isLoading: false,
      isLoadingMore: false,
      hasMoreMessages: true,
      messages: [],
      selectedTab: 'current',
      isEditing: false,
      editingMessageId: null,
      innerEntity: {
        systemMessageId: null,
        title: '',
        caption: '',
        message: '',
        type: '',
        linkText: '',
        linkUrl: '',
        startAtUtc: null,
        endAtUtc: null,
        isDraft: false,
        active: false,
        deleted: false
      },
      filter: {
        sortColumn: '',
        sortOrder: '',
        pageIndex: 1,
        pageSize: 10
      },
      messageTypes: [
        { value: 'info', label: 'Information' },
        { value: 'warning', label: 'Warning' },
        { value: 'success', label: 'Success' },
        { value: 'danger', label: 'Danger' }
      ],
      dateFormats: {
        title: 'MMMM YYYY',
        weekdays: 'W',
        navMonths: 'MMM',
        input: ['L LT', 'DD-MM-YYYY HH:mm', 'DD/MM/YYYY HH:mm'],
        data: ['L LT', 'DD-MM-YYYY HH:mm', 'DD/MM/YYYY HH:mm'],
        dayPopover: 'L'
      },
      isDeleteModalActive: false,
      messageToDelete: null
    }
  },
  computed: {
    filteredMessages() {
      if (!this.messages || this.messages.length === 0) {
        return []
      }

      if (this.selectedTab === 'archived') {
        // Archived: messages whose end date has passed
        const now = new Date()
        return this.messages.filter((m) => {
          if (m.isDraft || !m.endAtUtc) {
            return false
          }
          const endAt = m.endAtUtc instanceof Date ? m.endAtUtc : new Date(m.endAtUtc)
          return endAt < now
        })
      }

      // System Messages: current and upcoming messages (including drafts)
      const now = new Date()
      return this.messages.filter((m) => {
        if (m.isDraft || !m.endAtUtc) {
          return true
        }
        const endAt = m.endAtUtc instanceof Date ? m.endAtUtc : new Date(m.endAtUtc)
        return endAt >= now
      })
    },
    dateAttrs() {
      return [
        {
          key: 'today',
          highlight: {
            backgroundColor: '#ff8080',
            fillMode: 'light'
          },
          dates: new Date()
        }
      ]
    }
  },
  watch: {
    selectedTab() {
      // Reset scroll position when switching tabs
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = 0
        }
      })
    }
  },
  async created() {
    await this.loadMessages()
    await this.getEntity()
  },
  methods: {
    async getEntity() {
      if (this.editingMessageId) {
        await this.getStoreItem(this.editingMessageId)
      }
      if (!this.currentSnapshot) {
        await this.addStoreItem()
      }
      if (this.currentSnapshot) {
        this.innerEntity = _cloneDeep(this.currentSnapshot)
      }
    },
    async loadMessages() {
      this.isLoading = true
      this.filter.pageIndex = 1
      this.hasMoreMessages = true
      try {
        const result = await SystemMessageService.getMessages(this.filter)
        this.messages = result || []
        if (this.messages.length < this.filter.pageSize) {
          this.hasMoreMessages = false
        }
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to load messages')
        console.error('Error loading messages:', error)
      } finally {
        this.isLoading = false
      }
    },
    async loadMoreMessages() {
      if (this.isLoadingMore || !this.hasMoreMessages) return

      this.isLoadingMore = true
      this.filter.pageIndex++
      try {
        const result = await SystemMessageService.getMessages(this.filter)
        const newMessages = result || []
        if (newMessages.length > 0) {
          this.messages = [...this.messages, ...newMessages]
        }
        if (newMessages.length < this.filter.pageSize) {
          this.hasMoreMessages = false
        }
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to load more messages')
        console.error('Error loading more messages:', error)
        this.filter.pageIndex--
      } finally {
        this.isLoadingMore = false
      }
    },
    onScroll(event) {
      const container = event.target
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      if (scrollBottom < 100 && !this.isLoadingMore && this.hasMoreMessages) {
        this.loadMoreMessages()
      }
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    formatDisplayDate(date) {
      if (!date) return 'N/A'
      return this.$filters.formatDateTimeLocale(date, this.$userInfo.locale)
    },
    getMessageTypeClass(type) {
      const classes = {
        info: 'is-info',
        warning: 'is-warning',
        danger: 'is-danger',
        success: 'is-success'
      }
      return classes[type] || 'is-info'
    },
    validateForm() {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    async saveAsDraft() {
      if (!this.validateForm()) return

      this.$showSpinner('Saving...')
      try {
        const payload = this.preparePayload(true, false)
        if (this.isEditing) {
          await SystemMessageService.updateMessage(this.editingMessageId, payload)
        } else {
          await SystemMessageService.createMessage(payload)
        }
        this.saveSnapshot(_cloneDeep(this.innerEntity))
        this.$notification.openNotificationWithType('success', 'System Messages', 'Message saved as draft')
        this.reset()
        await this.getEntity()
        await this.loadMessages()
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to save message')
        console.error('Error saving message:', error)
      } finally {
        this.$hideSpinner()
      }
    },
    async saveAndActivate() {
      if (!this.validateForm()) return

      this.$showSpinner('Saving...')
      try {
        const payload = this.preparePayload(false, true)
        if (this.isEditing) {
          await SystemMessageService.updateMessage(this.editingMessageId, payload)
        } else {
          await SystemMessageService.createMessage(payload)
        }
        this.saveSnapshot(_cloneDeep(this.innerEntity))
        this.$notification.openNotificationWithType('success', 'System Messages', 'Message saved and activated')
        this.reset()
        await this.getEntity()
        await this.loadMessages()
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to save message')
        console.error('Error saving message:', error)
      } finally {
        this.$hideSpinner()
      }
    },
    preparePayload(isDraft, isActive) {
      return {
        systemMessageId: this.innerEntity.systemMessageId,
        title: this.innerEntity.title,
        caption: this.innerEntity.caption,
        message: this.innerEntity.message,
        type: this.innerEntity.type,
        linkText: this.innerEntity.linkText,
        linkUrl: this.innerEntity.linkUrl,
        startAtUtc: this.innerEntity.startAtUtc ? this.innerEntity.startAtUtc.toISOString() : null,
        endAtUtc: this.innerEntity.endAtUtc ? this.innerEntity.endAtUtc.toISOString() : null,
        isDraft: isDraft,
        active: isActive,
        deleted: this.innerEntity.deleted || false
      }
    },
    async editMessage(message) {
      this.isEditing = true
      this.editingMessageId = message.systemMessageId
      await this.getEntity()
      if (!this.innerEntity || !this.innerEntity.title) {
        this.innerEntity = {
          systemMessageId: message.systemMessageId,
          title: message.title,
          caption: message.caption,
          message: message.message || '',
          type: message.type,
          linkText: message.linkText || '',
          linkUrl: message.linkUrl || '',
          startAtUtc: message.startAtUtc ? new Date(message.startAtUtc) : null,
          endAtUtc: message.endAtUtc ? new Date(message.endAtUtc) : null,
          active: message.active,
          deleted: message.deleted || false
        }
        this.saveInitialSnapshots(_cloneDeep(this.innerEntity))
      }
      this.$v.$reset()
    },
    async cancelEdit() {
      this.reset()
      await this.getEntity()
    },
    reset() {
      this.isEditing = false
      this.editingMessageId = null
      this.innerEntity = {
        systemMessageId: null,
        title: '',
        caption: '',
        message: '',
        type: '',
        linkText: '',
        linkUrl: '',
        startAtUtc: null,
        endAtUtc: null,
        active: false,
        deleted: false
      }
      this.$v.$reset()
      this.clearSnapshots()
    },
    confirmDelete(message) {
      this.messageToDelete = message
      this.isDeleteModalActive = true
    },
    async deleteMessage() {
      if (!this.messageToDelete) return

      this.$showSpinner('Deleting...')
      try {
        await SystemMessageService.deleteMessage(this.messageToDelete.systemMessageId)
        this.$notification.openNotificationWithType('success', 'System Messages', 'Message deleted')
        this.isDeleteModalActive = false
        this.messageToDelete = null

        if (this.editingMessageId === this.messageToDelete?.systemMessageId) {
          this.reset()
          await this.getEntity()
        }

        await this.loadMessages()
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to delete message')
        console.error('Error deleting message:', error)
      } finally {
        this.$hideSpinner()
      }
    },
    async restoreMessage(message) {
      if (!message) return

      this.$showSpinner('Restoring...')
      try {
        const now = new Date()
        now.setSeconds(0, 0)
        if (now.getMinutes() % 5 !== 0) {
          now.setMinutes(now.getMinutes() + (5 - (now.getMinutes() % 5)))
        }
        const endDate = new Date(now.getTime() + 5 * 60 * 1000)
        const payload = {
          systemMessageId: message.systemMessageId,
          title: message.title,
          caption: message.caption,
          message: message.message,
          type: message.type,
          linkText: message.linkText,
          linkUrl: message.linkUrl,
          startAtUtc: now.toISOString(),
          endAtUtc: endDate.toISOString(),
          isDraft: false,
          active: true,
          deleted: message.deleted || false
        }
        await SystemMessageService.updateMessage(message.systemMessageId, payload)
        this.$notification.openNotificationWithType('success', 'System Messages', 'Message restored')
        await this.loadMessages()
        this.selectedTab = 'current'
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', 'Failed to restore message')
        console.error('Error restoring message:', error)
      } finally {
        this.$hideSpinner()
      }
    },
    handleToggleChange(message, event) {
      if (!message || message.isDraft) return
      const newActiveStatus = !!event.target.checked
      this.updateMessageStatus(message, newActiveStatus)
    },
    async toggleMessageStatus(message) {
      if (!message || message.isDraft) return

      const newActiveStatus = !message.active
      await this.updateMessageStatus(message, newActiveStatus)
    },
    async updateMessageStatus(message, newActiveStatus) {
      const statusText = newActiveStatus ? 'activated' : 'deactivated'

      this.$showSpinner(`${newActiveStatus ? 'Activating' : 'Deactivating'}...`)
      try {
        const payload = {
          systemMessageId: message.systemMessageId,
          title: message.title,
          caption: message.caption,
          message: message.message,
          type: message.type,
          linkText: message.linkText,
          linkUrl: message.linkUrl,
          startAtUtc: message.startAtUtc,
          endAtUtc: message.endAtUtc,
          active: newActiveStatus,
          deleted: message.deleted || false
        }
        await SystemMessageService.updateMessage(message.systemMessageId, payload)
        this.$notification.openNotificationWithType('success', 'System Messages', `Message ${statusText} successfully`)
        await this.loadMessages()
      } catch (error) {
        this.$notification.openNotificationWithType('danger', 'System Messages', `Failed to ${statusText} message`)
        console.error(`Error ${statusText} message:`, error)
      } finally {
        this.$hideSpinner()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.message-card {
  padding: 1rem;
  border: 1px solid var(--bulma-border);
  border-radius: 8px;
  background: var(--bulma-scheme-main);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.message-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;

  &.is-info {
    background-color: var(--bulma-info);
  }

  &.is-warning {
    background-color: var(--bulma-warning);
  }

  &.is-danger {
    background-color: var(--bulma-danger);
  }

  &.is-success {
    background-color: var(--bulma-success);
  }
}

.message-title {
  font-size: 1rem;
  color: var(--bulma-text-strong);
}

.message-content {
  line-height: 1.4;
}

.left-column {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 10%;
    right: 0;
    width: 1px;
    height: 80%;
    background-color: var(--bulma-border);
  }
}

.sub-title {
  font-weight: var(--bulma-weight-semibold);
  font-size: 1.25rem;
  white-space: nowrap;
}

.tabs ul li.sub-title a {
  border-bottom: 3px solid transparent;
  color: var(--bulma-text);
}

.tabs ul li.sub-title.is-active a {
  border-bottom-color: var(--bulma-primary);
  color: var(--bulma-text-strong);
}

.pretty.p-switch.p-fill input:not(:checked) ~ .state.p-primary::before {
  background-color: var(--bulma-border) !important;
}

.pretty.p-switch.p-fill input:not(:checked) ~ .state.p-primary label::after {
  background-color: #fff !important;
}

</style>

